import { Box, Typography } from '@dxb-web-ui-framework/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Csv as CsvIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import { formatAmount } from '@dxb-web-ui-framework/common/utils/Math';
import {
  formatAmountCellRenderer,
  payrollStatusCellRenderer,
  textWithIconCellRenderer,
  viewDetailsButtonRenderer,
} from '../../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import { TableGridConfig } from '../../../../content-composer/src/components/Generic/types';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';

const usePayrollGridConfig = (
  onToggleDrawer: Function,
  onClickPayrollMobileListItem: (data: any) => void,
  classes: any,
): TableGridConfig => {
  const language = useLanguage();
  const locale = language.cultureName;
  const { t } = useTranslation();

  return {
    gridOptions: {
      ariaTitle: t('payrollPage.PayrollTable.UsePayrollGridConfig.GridOptions.AriaTitle'),
      dataTestId: 'Bulk-transfers-table',
      noResultsMsg: t('payrollPage.PayrollTable.UsePayrollGridConfig.GridOptions.NoResultMessage'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    columnDefs: [
      {
        id: 'From',
        field: 'accountNumber',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.From'),
        cellRenderer: (rowData: any, columnDef: any) => (
          <Typography variant='subtitle2'>{rowData[columnDef.field]}</Typography>
        ),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Status',
        field: 'status',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.Status'),
        cellRenderer: payrollStatusCellRenderer,
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'File type',
        field: 'fileType',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.FileType'),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'File name',
        field: 'fileName',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.FileName'),
        cellRenderer: textWithIconCellRenderer(<CsvIcon />),
        columnHeaderCellProps: {
          colSpan: 1,
        },
        cellProps: {
          colSpan: 1,
        },
      },
      {
        id: 'Amount',
        field: 'totalAmount',
        label: t('payrollPage.PayrollTable.UsePayrollGridConfig.ColumnDefs.Label.Amount'),
        columnHeaderCellProps: { align: 'right', colSpan: 1 },
        cellProps: { align: 'right', colSpan: 1 },
        // TODO: handle 'N/A'.
        cellRenderer: (rowData: any, columnDef: any) =>
          rowData.currencyIso !== 'N/A'
            ? formatAmount(rowData[columnDef.field], rowData['currencyIso'], 2, locale)
            : rowData[columnDef.field],
      },
      {
        id: 'View Icon',
        field: 'actions',
        label: '',
        cellProps: { align: 'right' },
        cellRenderer: viewDetailsButtonRenderer(
          onToggleDrawer,
          'id',
          false,
          t('payrollPage.payrollDetails.VisibilityIconBtnTitle'),
          t('payrollPage.payrollDetails.VisibilityIconBtnAriaLabel'),
        ),
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'accountNumber',
          value: 'accountNumber',
          valueAreaLabel: 'from',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          valueTransformer: (data: any) => (
            <Box display='flex' flexDirection='column' component='span'>
              <Box mt={0.5} component={'span'} />
              <Typography
                color='textSecondary'
                component='p'
                aria-label={String(data.accountNumber)}
                className={classes.typographyValue}>
                {data.from}
              </Typography>
              <Box mt={0.5} component={'span'} />
              <Typography
                color='textSecondary'
                component='p'
                aria-label={data.fileName}
                className={classes.typographyValue}>
                {data.fileName}
              </Typography>
            </Box>
          ),
        },
        {
          label: 'totalAmount',
          value: 'status',
          valueAreaLabel: 'status',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelTransformer: (data: any) => (
            <Box display={'flex'} justifyContent={'flex-end'}>
              {data.currencyIso !== 'N/A'
                ? formatAmountCellRenderer(locale, true, 'currencyIso')(data, { field: 'totalAmount' })
                : data.totalAmount}
            </Box>
          ),
          valueTransformer: (data: any) => (
            <Box mt={3} display='flex' component='span' justifyContent={'flex-end'}>
              {payrollStatusCellRenderer(data, { field: 'status' })}
            </Box>
          ),
        },
      ],
      listItemOnSelect: onClickPayrollMobileListItem,
    },
  };
};

export { usePayrollGridConfig };
